// =============================================================================
// cloud-sync-ui.js – Cloud sync panel rendering and event handling
// =============================================================================
// Extracted from settings.js. Handles the cloud sync section within the
// Settings tab: rendering the panel, login/logout/push/pull/passphrase events,
// and the real-time cross-device listener.
// =============================================================================

import {
  getCurrentUser,
  getSyncStatus,
  isFirebaseReady,
  signInWithGoogle,
  signOut,
  saveToCloud,
  loadFromCloud,
  deleteCloudData,
  listenForCloudChanges,
  onAuthStateChanged,
  setSyncPassphrase,
  getSyncPassphrase
} from "./firebase-sync.js";
import {
  getNotificationConfig,
  saveNotificationConfig,
  sanitizeNotifications
} from "./notifications.js";
import { sanitizeSettings, deepEqual } from "./settings-logic";
import { setSuppressCloudWrite } from "./store";
import { escapeHtml } from "./utils.js";

// ─── Render ─────────────────────────────────────────────────────────────────

export function renderCloudSyncSection(_cfg) {
  const user = getCurrentUser();

  if (!isFirebaseReady()) {
    return `
      <section class="panel" id="cloud-sync-section">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Cloud Sync</p>
            <h2>Sincronizzazione preferenze</h2>
            <p>Caricamento Firebase in corso...</p>
          </div>
        </div>
      </section>`;
  }

  if (!user) {
    return `
      <section class="panel" id="cloud-sync-section">
        <div class="panel-heading">
          <div>
            <p class="section-eyebrow">Cloud Sync</p>
            <h2>Sincronizza tra dispositivi</h2>
            <p>Accedi con Google per salvare le preferenze nel cloud e sincronizzarle su tutti i tuoi dispositivi.</p>
          </div>
        </div>
        <div class="button-grid">
          <button type="button" class="btn primary" data-cloud-login style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Accedi con Google
          </button>
        </div>
      </section>`;
  }

  // User is logged in
  const photoHtml = `<div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(34, 211, 238, 0.12); border: 2px solid var(--accent); display: flex; align-items: center; justify-content: center; color: var(--accent);" title="Avatar Generico">
    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
  </div>`;

  const passphrase = getSyncPassphrase();
  const isLocked = window._cloud_is_encrypted === true && !passphrase;

  let cryptoStatusHtml = "";
  if (passphrase) {
    cryptoStatusHtml = `
      <div style="margin-top: 12px; padding: 12px; border: 1px solid rgba(34,197,94,0.3); border-radius: var(--radius); background: rgba(34,197,94,0.06); display: flex; flex-direction: column; gap: 6px;">
        <span style="font-size: 0.85rem; font-weight: 700; color: var(--ok); display: flex; align-items: center; gap: 6px;">🔒 Crittografia Zero-Knowledge Attiva (AES-256)</span>
        <span style="font-size: 0.75rem; color: var(--muted);">Le tue preferenze vengono cifrate sul tuo dispositivo prima di essere caricate. Nessun altro può leggerle.</span>
        <div style="display: flex; gap: 8px; margin-top: 4px;">
          <input type="password" value="${escapeHtml(passphrase)}" readonly style="flex: 1; min-height: 32px; border: 1px solid var(--line); border-radius: var(--radius); background: rgba(255,255,255,0.02); color: var(--muted); padding: 4px 8px; font-size: 0.8rem; font-family: monospace;">
          <button type="button" class="btn secondary" data-sync-passphrase-change style="padding: 2px 8px; font-size: 0.75rem; min-height: 32px;">Modifica</button>
        </div>
      </div>`;
  } else if (window._cloud_is_encrypted === true) {
    cryptoStatusHtml = `
      <div style="margin-top: 12px; padding: 12px; border: 1px solid rgba(239,68,68,0.4); border-radius: var(--radius); background: rgba(239,68,68,0.06); display: flex; flex-direction: column; gap: 8px;">
        <span style="font-size: 0.85rem; font-weight: 700; color: #fca5a5; display: flex; align-items: center; gap: 6px;">🔒 Sincronizzazione Bloccata</span>
        <span style="font-size: 0.75rem; color: var(--muted);">I tuoi dati nel cloud sono protetti da crittografia. Inserisci la tua passphrase per sbloccare la sincronizzazione.</span>
        <div style="display: flex; gap: 8px;">
          <input type="password" placeholder="Inserisci Passphrase per sbloccare" data-sync-passphrase-unlock-input style="flex: 1; min-height: 32px; border: 1px solid rgba(239,68,68,0.4); border-radius: var(--radius); background: var(--surface); color: var(--text); padding: 4px 8px; font-size: 0.8rem;">
          <button type="button" class="btn primary" data-unlock-passphrase-btn style="padding: 2px 10px; font-size: 0.75rem; min-height: 32px; background: var(--accent); color: var(--bg); border: 1px solid var(--accent);">Sblocca</button>
        </div>
        <div style="margin-top: 4px; border-top: 1px solid var(--line); padding-top: 8px; display: flex; justify-content: space-between; align-items: center;">
          <small style="color: var(--quiet); font-size: 0.72rem;">Chiave persa o dimenticata?</small>
          <button type="button" class="text-btn" data-reset-encrypted-cloud style="color: var(--danger); font-size: 0.72rem; text-decoration: underline; background: none; border: none; cursor: pointer;">Resetta dati cloud</button>
        </div>
      </div>`;
  } else {
    cryptoStatusHtml = `
      <div style="margin-top: 12px; padding: 12px; border: 1px solid rgba(245,158,11,0.3); border-radius: var(--radius); background: rgba(245,158,11,0.06); display: flex; flex-direction: column; gap: 8px;">
        <span style="font-size: 0.85rem; font-weight: 700; color: #f59e0b; display: flex; align-items: center; gap: 6px;">⚠️ Sincronizzazione in Chiaro</span>
        <span style="font-size: 0.75rem; color: var(--muted);">Imposta una passphrase per crittografare i tuoi dati sul tuo browser (Zero-Knowledge) prima del caricamento nel cloud.</span>
        <div style="display: flex; gap: 8px;">
          <input type="password" placeholder="Passphrase di Sincronizzazione" data-sync-passphrase-input style="flex: 1; min-height: 32px; border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); color: var(--text); padding: 4px 8px; font-size: 0.8rem;">
          <button type="button" class="btn primary" data-save-passphrase-btn style="padding: 2px 10px; font-size: 0.75rem; min-height: 32px;">Proteggi Dati</button>
        </div>
      </div>`;
  }

  return `
    <section class="panel" id="cloud-sync-section">
      <div class="panel-heading">
        <div>
          <p class="section-eyebrow">Cloud Sync</p>
          <h2>Profilo connesso</h2>
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 12px; padding: 12px; border: 1px solid var(--line); border-radius: var(--radius); background: rgba(16,24,40,0.42);">
        ${photoHtml}
        <div style="flex: 1; min-width: 0;">
          <strong style="display: block; font-size: 0.92rem;">${escapeHtml(user.displayName || "Utente")}</strong>
          <small style="color: var(--muted); font-size: 0.75rem;">${escapeHtml(user.email || "")}</small>
        </div>
        <span style="font-size: 0.68rem; font-weight: 700; color: var(--ok); background: rgba(34,197,94,0.12); padding: 3px 8px; border-radius: 999px;">Online</span>
      </div>
      ${cryptoStatusHtml}
      <div class="button-grid" style="margin-top: 12px;">
        <button type="button" class="btn primary" data-cloud-push ${isLocked ? "disabled style='opacity:0.4; cursor:not-allowed;'" : ""}>Salva nel cloud ora</button>
        <button type="button" class="btn secondary" data-cloud-pull ${isLocked ? "disabled style='opacity:0.4; cursor:not-allowed;'" : ""}>Carica dal cloud</button>
        <button type="button" class="btn secondary" data-cloud-logout>Disconnetti</button>
        <button type="button" class="btn secondary" data-cloud-delete style="color: var(--danger);">Elimina dati cloud</button>
      </div>
      <div style="margin-top: 12px; border-top: 1px solid var(--line); padding-top: 8px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
        <small style="color: var(--quiet); font-size: 0.72rem;">Le preferenze vengono sincronizzate automaticamente.</small>
        <a href="./privacy.html" target="_blank" rel="noopener" style="color: var(--accent); font-size: 0.75rem; text-decoration: underline; font-weight: 500;">Informativa Privacy e GDPR 🔒</a>
      </div>
    </section>`;
}

// ─── Events ─────────────────────────────────────────────────────────────────

/**
 * Bind cloud sync events. Requires a getContext function that returns
 * { state, saveFn, cfg, lineData, lineConfig, renderSettings }.
 */
export function bindCloudSyncEvents(container, getContext) {
  const { state, saveFn, cfg, lineData, lineConfig, renderSettings } = getContext();

  ensureAuthStateBound(getContext);

  const loginBtn = container.querySelector("[data-cloud-login]");
  if (loginBtn && !loginBtn.__has_click) {
    loginBtn.__has_click = true;
    loginBtn.addEventListener("click", async () => {
      try {
        const user = await signInWithGoogle();
        if (user) {
          const cloudPayload = await loadFromCloud();
          const cloudSettings = cloudPayload?.settings ?? null;
          const cloudNotifs = cloudPayload?.notifications ?? null;
          if (cloudPayload && (cloudSettings || cloudNotifs)) {
            const useCloud = confirm(
              "Trovate preferenze nel cloud. Vuoi sovrascrivere quelle locali con quelle dal cloud?"
            );
            if (useCloud) {
              if (cloudSettings) saveFn(sanitizeSettings(cloudSettings, cfg));
              if (cloudNotifs) saveNotificationConfig(sanitizeNotifications(cloudNotifs));
            } else {
              saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
            }
          } else if (cloudPayload && cloudPayload.isEncrypted) {
            // remains locked
          } else {
            saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
          }
          startCloudListener(getContext);
          renderSettings(state, saveFn, cfg, lineData, lineConfig);
        }
      } catch (e) {
        alert("Errore durante il login: " + e.message);
      }
    });
  }

  const logoutBtn = container.querySelector("[data-cloud-logout]");
  if (logoutBtn && !logoutBtn.__has_click) {
    logoutBtn.__has_click = true;
    logoutBtn.addEventListener("click", async () => {
      await signOut();
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  }

  const pushBtn = container.querySelector("[data-cloud-push]");
  if (pushBtn && !pushBtn.__has_click) {
    pushBtn.__has_click = true;
    pushBtn.addEventListener("click", () => {
      saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
      alert("Preferenze salvate nel cloud.");
    });
  }

  const pullBtn = container.querySelector("[data-cloud-pull]");
  if (pullBtn && !pullBtn.__has_click) {
    pullBtn.__has_click = true;
    pullBtn.addEventListener("click", async () => {
      const cloudPayload = await loadFromCloud();
      const cloudSettings = cloudPayload?.settings ?? null;
      const cloudNotifs = cloudPayload?.notifications ?? null;
      if (cloudPayload && (cloudSettings || cloudNotifs)) {
        if (cloudSettings) saveFn(sanitizeSettings(cloudSettings, cfg));
        if (cloudNotifs) saveNotificationConfig(sanitizeNotifications(cloudNotifs));
        alert("Preferenze caricate dal cloud.");
        renderSettings(state, saveFn, cfg, lineData, lineConfig);
      } else {
        alert("Nessuna preferenza trovata nel cloud.");
      }
    });
  }

  const deleteBtn = container.querySelector("[data-cloud-delete]");
  if (deleteBtn && !deleteBtn.__has_click) {
    deleteBtn.__has_click = true;
    deleteBtn.addEventListener("click", async () => {
      if (
        !confirm(
          "Eliminare definitivamente i dati dal cloud? Le preferenze locali rimarranno intatte."
        )
      )
        return;
      await deleteCloudData();
      alert("Dati cloud eliminati.");
    });
  }

  const savePassBtn = container.querySelector("[data-save-passphrase-btn]");
  if (savePassBtn && !savePassBtn.__has_click) {
    savePassBtn.__has_click = true;
    savePassBtn.addEventListener("click", () => {
      const input = container.querySelector("[data-sync-passphrase-input]");
      const val = input ? input.value.trim() : "";
      if (val.length < 4) {
        alert("La passphrase deve essere lunga almeno 4 caratteri.");
        return;
      }
      if (
        !confirm(
          "Confermi l'impostazione di questa Passphrase?\n\n⚠️ Se perdi la passphrase, i tuoi dati cifrati nel cloud saranno IRRECUPERABILI."
        )
      )
        return;
      setSyncPassphrase(val);
      saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
      alert("Passphrase impostata! Dati protetti da crittografia Zero-Knowledge.");
      renderSettings(state, saveFn, cfg, lineData, lineConfig);
    });
  }

  const changePassBtn = container.querySelector("[data-sync-passphrase-change]");
  if (changePassBtn && !changePassBtn.__has_click) {
    changePassBtn.__has_click = true;
    changePassBtn.addEventListener("click", () => {
      const newPass = prompt("Nuova Passphrase (vuoto per disattivare la crittografia):");
      if (newPass === null) return;
      const trimmed = newPass.trim();
      if (trimmed === "") {
        if (confirm("Disattivare la crittografia? I dati torneranno in chiaro.")) {
          setSyncPassphrase(null);
          saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
          alert("Crittografia disattivata.");
          renderSettings(state, saveFn, cfg, lineData, lineConfig);
        }
      } else {
        if (trimmed.length < 4) {
          alert("Minimo 4 caratteri.");
          return;
        }
        if (
          !confirm(
            "Aggiornare la Passphrase?\n\n⚠️ Se perdi la nuova passphrase, i dati saranno IRRECUPERABILI."
          )
        )
          return;
        setSyncPassphrase(trimmed);
        saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
        alert("Passphrase aggiornata.");
        renderSettings(state, saveFn, cfg, lineData, lineConfig);
      }
    });
  }

  const unlockBtn = container.querySelector("[data-unlock-passphrase-btn]");
  if (unlockBtn && !unlockBtn.__has_click) {
    unlockBtn.__has_click = true;
    unlockBtn.addEventListener("click", async () => {
      const input = container.querySelector("[data-sync-passphrase-unlock-input]");
      const val = input ? input.value.trim() : "";
      if (!val) {
        alert("Inserisci la passphrase.");
        return;
      }
      setSyncPassphrase(val);
      try {
        const cloudPayload = await loadFromCloud();
        const cloudSettings = cloudPayload?.settings ?? null;
        const cloudNotifs = cloudPayload?.notifications ?? null;
        if (cloudPayload && (cloudSettings || cloudNotifs)) {
          const useCloud = confirm(
            "Sbloccato! Sovrascrivere le preferenze locali con quelle cloud?"
          );
          if (useCloud) {
            if (cloudSettings) saveFn(sanitizeSettings(cloudSettings, cfg));
            if (cloudNotifs) saveNotificationConfig(sanitizeNotifications(cloudNotifs));
          } else {
            saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
          }
        } else {
          saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
        }
        window.dispatchEvent(
          new CustomEvent("trasporti:sync-status", { detail: { status: "synced" } })
        );
        alert("Sincronizzazione sbloccata!");
        renderSettings(state, saveFn, cfg, lineData, lineConfig);
      } catch (err) {
        setSyncPassphrase(null);
        alert(
          err.message === "passphrase_incorrect" ? "Passphrase errata." : "Errore: " + err.message
        );
      }
    });
  }

  const resetBtn = container.querySelector("[data-reset-encrypted-cloud]");
  if (resetBtn && !resetBtn.__has_click) {
    resetBtn.__has_click = true;
    resetBtn.addEventListener("click", async () => {
      if (
        !confirm(
          "⚠️ Il reset eliminerà definitivamente i dati crittografati dal cloud. I dati locali restano. Procedere?"
        )
      )
        return;
      try {
        await deleteCloudData();
        setSyncPassphrase(null);
        window._cloud_is_encrypted = false;
        window.dispatchEvent(
          new CustomEvent("trasporti:sync-status", { detail: { status: "synced" } })
        );
        alert("Dati cloud resettati.");
        renderSettings(state, saveFn, cfg, lineData, lineConfig);
      } catch (err) {
        alert("Errore: " + err.message);
      }
    });
  }
}

// ─── Cloud listener (cross-device sync) ─────────────────────────────────────

let _unsubscribeCloudListener = null;
let _authStateBound = false;

export function ensureAuthStateBound(getContext) {
  if (_authStateBound) return;
  _authStateBound = true;
  onAuthStateChanged((user) => {
    if (user) startCloudListener(getContext);
    else if (_unsubscribeCloudListener) {
      _unsubscribeCloudListener();
      _unsubscribeCloudListener = null;
    }
  });
}

export function startCloudListener(getContext) {
  if (_unsubscribeCloudListener) _unsubscribeCloudListener();
  _unsubscribeCloudListener = listenForCloudChanges((cloudPayload) => {
    const { state, saveFn, cfg, lineData, lineConfig, renderSettings } = getContext();
    const rawSettings = cloudPayload?.settings !== undefined ? cloudPayload.settings : cloudPayload;
    const rawNotifs = cloudPayload?.notifications !== undefined ? cloudPayload.notifications : null;
    const incomingSettings = sanitizeSettings(rawSettings, cfg);
    const incomingNotifs =
      rawNotifs !== null ? sanitizeNotifications(rawNotifs) : getNotificationConfig();

    if (
      deepEqual(incomingSettings, state.settings) &&
      deepEqual(incomingNotifs, getNotificationConfig())
    )
      return;

    setSuppressCloudWrite(true);
    try {
      state.settings = incomingSettings;
      try {
        localStorage.setItem("trasporti_settings", JSON.stringify(state.settings));
      } catch (_e) {
        /* ignore */
      }
      if (rawNotifs !== null) saveNotificationConfig(incomingNotifs);
      if (state.currentTab === "settings") renderSettings(state, saveFn, cfg, lineData, lineConfig);
      else document.dispatchEvent(new CustomEvent("trasporti:settings-changed"));
    } finally {
      setSuppressCloudWrite(false);
    }
    console.log("[FirebaseSync] Preferenze aggiornate da altro dispositivo.");
  });
}
